/**
 * Chips input — type, Enter to add, × to remove. Used for banned words
 * and signature phrases (AI-09 §C1 step 2). The quota is enforced
 * inline: at the limit the input disables and a hint explains why,
 * matching the server-side §G2 422 it prevents.
 */
import { useState, type KeyboardEvent } from "react";

export interface ChipsInputProps {
  id: string;
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  maxItems: number;
  maxLength?: number;
  placeholder?: string;
  limitHint: string;
  removeLabel: (chip: string) => string;
}

export function ChipsInput({
  id,
  label,
  value,
  onChange,
  maxItems,
  maxLength = 200,
  placeholder,
  limitHint,
  removeLabel,
}: ChipsInputProps): JSX.Element {
  const [draft, setDraft] = useState("");
  const atLimit = value.length >= maxItems;

  function add(): void {
    const chip = draft.trim().slice(0, maxLength);
    if (chip === "" || atLimit) return;
    if (value.some((v) => v.toLowerCase() === chip.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...value, chip]);
    setDraft("");
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    }
    if (e.key === "Backspace" && draft === "" && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-neutral-700">
        {label}{" "}
        <span className="font-normal text-neutral-400">
          ({value.length}/{maxItems})
        </span>
      </label>
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((chip) => (
            <li
              key={chip}
              className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2.5 py-0.5 text-sm text-neutral-800"
            >
              <span className="max-w-[16rem] truncate">{chip}</span>
              <button
                type="button"
                aria-label={removeLabel(chip)}
                className="text-neutral-400 hover:text-neutral-700"
                onClick={() => onChange(value.filter((v) => v !== chip))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id={id}
        type="text"
        value={draft}
        disabled={atLimit}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={add}
        className="block w-full rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-neutral-50"
      />
      {atLimit && <p className="text-xs text-warning-600">{limitHint}</p>}
    </div>
  );
}
